import { Button, Col, Form, Input, Row, Select } from "antd";

import { Modal, message } from "antd";
import { useNavigate } from "react-router";
import { useDeleteCourseMutation } from "../../../../app/features/admin/admin.api";
import type { IPublicCourseResponse } from "../../../../app/features/adminContent/adminContent.types";
import CourseCurriculum from "./CourseCurriculum";

interface OverviewProps {
  courseDetails?: IPublicCourseResponse;
  isLoading?: boolean;
}

export default function Overview({ courseDetails, isLoading }: OverviewProps) {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [deleteCourse, { isLoading: isDeleting }] = useDeleteCourseMutation();

  const handleDeleteCourse = () => {
    if (!courseDetails?.id) return;

    Modal.confirm({
      title: "Are you sure you want to delete this course?",
      content:
        "This action cannot be undone. All course content will be permanently removed.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteCourse({ id: courseDetails.id }).unwrap();
          message.success("Course deleted successfully");
          navigate("/admin/my-course");
        } catch (error) {
          message.error("Failed to delete course");
        }
      },
      okButtonProps: { loading: isDeleting },
    });
  };

  // Extract highlights by type
  const learnings =
    courseDetails?.CourseHighlight?.filter((h) => h.type === "LEARNING")
      .map((h) => `• ${h.text}`)
      .join("\n") || "";

  const targetAudience =
    courseDetails?.CourseHighlight?.filter((h) => h.type === "AUDIENCE")
      .map((h) => `• ${h.text}`)
      .join("\n") || "";

  const includes =
    courseDetails?.CourseHighlight?.filter((h) => h.type === "INCLUDES")
      .map((h) => `• ${h.text}`)
      .join("\n") || "";

  // Calculate total sections, lectures, and duration
  // const totalSections = courseDetails?.CourseSection?.length || 0;
  // const totalLectures =
  //   courseDetails?.CourseSection?.reduce(
  //     (acc, section) => acc + (section.Lecture?.length || 0),
  //     0,
  //   ) || 0;
  // const totalDurationSec =
  //   courseDetails?.CourseSection?.reduce(
  //     (acc, section) => acc + (section.duration || 0),
  //     0,
  //   ) || 0;

  // Convert seconds to hours and minutes
  // const hours = Math.floor(totalDurationSec / 3600);
  // const minutes = Math.floor((totalDurationSec % 3600) / 60);
  // const durationLabel = `${hours}h ${minutes}m`;

  if (isLoading) {
    return (
      <div className="text-center py-8 text-gray-400">
        Loading course details...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Form
        form={form}
        layout="vertical"
        size="large"
        className="custom-form space-y-6"
        initialValues={{
          category: courseDetails?.Category?.name || "-",
          subCategory: courseDetails?.SubCategory?.name || "-",
          level: courseDetails?.level || "-",
          language: courseDetails?.language || "-",
          title: courseDetails?.title || "-",
          description: courseDetails?.description || "-",
          learnings: learnings || "-",
          targetAudience: targetAudience || "-",
          includes: includes || "-",
        }}
      >
        {/* Promotional Video Banner */}
        {courseDetails?.promotionalVideo && (
          <div className="w-full aspect-video rounded-xl overflow-hidden shadow-sm mb-6 bg-black">
            <video
              src={courseDetails.promotionalVideo}
              controls
              className="w-full h-full object-contain"
              poster={courseDetails.bannerUrl || courseDetails.thumbnailUrl}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        )}

        {/* General Section */}
        <div className="p-4 bg-white rounded-xl shadow-[0px_3px_16px_0px_rgba(0,0,0,0.04)] border-none">
          <h3 className="text-theme-slate-700 text-xl font-bold mb-4">
            General
          </h3>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label="Primary Category" name="category">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Sub-category" name="subCategory">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Course Level" name="level">
                <Select disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Teaching Language" name="language">
                <Select disabled />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Course Title" name="title">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Description" name="description">
                <Input.TextArea rows={4} disabled />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="What Learners Will Learn" name="learnings">
                <Input.TextArea rows={4} disabled />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Who This Course is For" name="targetAudience">
                <Input.TextArea rows={4} disabled />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="This Course Includes" name="includes">
                <Input.TextArea rows={4} disabled />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Pricing Section */}
        <div className="p-4 bg-white rounded-xl shadow-[0px_3px_16px_0px_rgba(0,0,0,0.04)] border-none">
          <h3 className="text-theme-slate-700 text-xl font-bold mb-4">
            Pricing
          </h3>

          <Row gutter={24}>
            <Col span={8}>
              <Form.Item label="Course Price (BDT)">
                <Input disabled value={courseDetails?.price || "-"} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Offer Price (BDT)">
                <Input disabled value={courseDetails?.offerPrice || "-"} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Currency">
                <Input disabled value={courseDetails?.currency || "BDT"} />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Media Section */}
        <div className="p-4 bg-white rounded-xl shadow-[0px_3px_16px_0px_rgba(0,0,0,0.04)] border-none">
          <h3 className="text-theme-slate-700 text-xl font-bold mb-4">Media</h3>
          <Row gutter={24}>
            <Col span={12}>
              <p className="mb-2 text-sm font-medium">Course Thumbnail</p>
              {courseDetails?.thumbnailUrl ? (
                <img
                  src={courseDetails.thumbnailUrl}
                  alt="Thumbnail"
                  className="w-full h-64 object-cover rounded-lg"
                />
              ) : (
                <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                  No thumbnail available
                </div>
              )}
            </Col>
            <Col span={12}>
              <p className="mb-2 text-sm font-medium">Course Banner</p>
              {courseDetails?.bannerUrl ? (
                <img
                  src={courseDetails.bannerUrl}
                  alt="Thumbnail"
                  className="w-full h-64 object-cover rounded-lg"
                />
              ) : (
                <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                  No thumbnail available
                </div>
              )}
            </Col>
          </Row>
        </div>

        {/* Course Content */}
        <div className="p-4 bg-white rounded-xl shadow-[0px_3px_16px_0px_rgba(0,0,0,0.04)] border-none">
          <h3 className="text-theme-slate-700 text-xl font-bold mb-1.5">
            Course Content
          </h3>
          <div className="mb-4">
            <span className="text-theme-slate-600 text-sm font-normal">
              {courseDetails?.CourseSection?.length || 0} sections
            </span>
          </div>
          <CourseCurriculum course={courseDetails} isLoading={isLoading} />
        </div>

        {/* Delete & Deactivation */}
        <div className="p-4 bg-white rounded-xl shadow-[0px_3px_16px_0px_rgba(0,0,0,0.04)] border-none">
          <h3 className="text-theme-slate-700 text-xl font-bold mb-4">
            Delete & Deactivation
          </h3>
          <div className="space-y-4">
            {/* <div className="flex items-center justify-between p-4 border border-red-100 bg-red-50/30 rounded-lg">
              <div>
                <h4 className="font-medium text-gray-900">Unpublish Course</h4>
                <p className="text-sm text-gray-500">
                  Make this course unavailable to new learners
                </p>
              </div>
              <Button className="text-red-600 border-red-200 bg-white hover:bg-red-50">
                Unpublish
              </Button>
            </div>

            <div className="flex items-center justify-between p-4 border border-red-100 bg-red-50/30 rounded-lg">
              <div>
                <h4 className="font-medium text-gray-900">Archive Course</h4>
                <p className="text-sm text-gray-500">
                  Move this course to archived courses
                </p>
              </div>
              <Button className="text-red-600 border-red-200 bg-white hover:bg-red-50">
                Archive
              </Button>
            </div> */}

            <div className="flex items-center justify-between p-4 border border-red-100 bg-red-50/30 rounded-lg">
              <div>
                <h4 className="font-medium text-gray-900">Delete Course</h4>
                <p className="text-sm text-gray-500">
                  Permanently delete this course and all its content
                </p>
              </div>
              <Button
                danger
                className="text-red-600 border-red-200 bg-white hover:bg-red-50"
                onClick={handleDeleteCourse}
                loading={isDeleting}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
}
